import { Body, Box, Heading } from '@hover/blueprint';
import React, { useEffect } from 'react';

import type { MeasurementTitle } from 'src/modules/homeownerApp/components/Projects/Project/Measurements';
import {
  MEASUREMENT_FOCUS_ELEMENTS,
  Measurements,
} from 'src/modules/homeownerApp/components/Projects/Project/Measurements';
import {
  JobModelStates,
  useGetJobHomeownerMeasurementsQuery,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { logError } from 'src/shared/telemetry/logger';

import { ThreeDModel } from '../../ThreeDModel';

import {
  ProjectAccordion,
  ProjectAccordionHeader,
  ProjectAccordionPanel,
} from './ProjectAccordion';
import { PROJECT_DETAILS_PADDING } from './ProjectCardSection';

type KeyMeasurementsProps = {
  jobId: number;
  projectId: number;
};

const IN_PROGRESS: JobModelStates[] = [
  JobModelStates.Paying,
  JobModelStates.Processing,
  JobModelStates.Uploading,
  JobModelStates.ProcessingUpload,
  JobModelStates.ProcessingUploadForImprovements,
  JobModelStates.RequestingCorrections,
  JobModelStates.RequestingImprovements,
  JobModelStates.Submitting,
  JobModelStates.WaitingApproval,
];

export const KeyMeasurements = ({ jobId, projectId }: KeyMeasurementsProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const [activeItem, setActiveItem] = React.useState<MeasurementTitle | null>(
    null,
  );
  const { data, error } = useGetJobHomeownerMeasurementsQuery({
    variables: {
      id: Number(jobId),
    },
  });

  useEffect(() => {
    if (error) logError(error);
  }, [error]);

  // Reset active item to be null when jobId changes
  useEffect(() => {
    setActiveItem(null);
  }, [jobId]);

  const homeownerMeasurementData = data?.jobs?.results[0];
  const isInProgress = IN_PROGRESS.includes(
    homeownerMeasurementData?.state as JobModelStates,
  );
  const fullJson = homeownerMeasurementData?.homeownerMeasurements?.fullJson;
  const threeDFiles = homeownerMeasurementData?.threeDFiles;
  const isAccordionDisabled = !fullJson || !threeDFiles;

  const focusElement = activeItem
    ? MEASUREMENT_FOCUS_ELEMENTS[activeItem]
    : undefined;

  return isInProgress ? (
    <Box mt={500} px={{ base: 500, tablet: 800 }}>
      <Box
        alignSelf="center"
        backgroundColor="neutral.50"
        borderRadius="400"
        flexDirection="column"
        height="100%"
        overflow="hidden"
        position="relative"
        px={500}
        py={600}
        width="100%"
      >
        <Heading>{t('projects.keyMeasurements.buildingYourModel')}</Heading>
        <Body color="neutral.600" size={300}>
          {t('projects.keyMeasurements.weWillNotifyYou')}
        </Body>
      </Box>
    </Box>
  ) : (
    <ProjectAccordion defaultOpen disabled={isAccordionDisabled}>
      <ProjectAccordionHeader label={t('projects.keyMeasurements.title')} />
      {threeDFiles && (
        <Box px={PROJECT_DETAILS_PADDING}>
          <ThreeDModel
            focusElement={focusElement}
            key={jobId}
            threeDFiles={threeDFiles}
          />
        </Box>
      )}
      <ProjectAccordionPanel>
        <Measurements
          activeItem={activeItem}
          fullJson={fullJson}
          jobId={jobId}
          projectId={projectId}
          px={PROJECT_DETAILS_PADDING}
          setActiveItem={setActiveItem}
        />
      </ProjectAccordionPanel>
    </ProjectAccordion>
  );
};
